/**
 * Google Analytics 4 和 Search Console 跟踪工具
 * 用于AEO性能监控
 */

// Google Analytics 4 配置
// 注意：在生产环境中，将 GA_MEASUREMENT_ID 替换为您的实际测量ID
const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || 'G-XXXXXXXXXX';

/**
 * 初始化 Google Analytics 4
 */
export const initGA = () => {
  if (typeof window !== 'undefined' && GA_MEASUREMENT_ID && GA_MEASUREMENT_ID !== 'G-XXXXXXXXXX') {
    // 加载 gtag.js
    const script1 = document.createElement('script');
    script1.async = true;
    script1.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    document.head.appendChild(script1);

    // 初始化 dataLayer 和 gtag
    window.dataLayer = window.dataLayer || [];
    function gtag() {
      window.dataLayer.push(arguments);
    }
    window.gtag = gtag;
    gtag('js', new Date());
    gtag('config', GA_MEASUREMENT_ID, {
      page_path: window.location.pathname,
    });
  }
};

/**
 * 跟踪页面浏览
 * @param {string} pagePath - 页面路径
 * @param {string} pageTitle - 页面标题
 * @param {string} question - 核心问题（用于AEO追踪）
 */
export const trackPageView = (pagePath, pageTitle, question = '') => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('config', GA_MEASUREMENT_ID, {
      page_path: pagePath,
      page_title: pageTitle,
    });

    // 记录核心问题展示（用于AEO分析）
    if (question) {
      window.gtag('event', 'page_impression', {
        event_category: 'AEO',
        event_label: question,
        question: question,
        page_path: pagePath,
      });
    }
  }
};

/**
 * 跟踪FAQ交互（展开/折叠）
 * @param {string} question - FAQ问题
 * @param {string} action - 操作类型 ('expand' 或 'collapse')
 * @param {string} pagePath - 当前页面路径
 */
export const trackFAQInteraction = (question, action, pagePath = window.location.pathname) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'faq_interaction', {
      event_category: 'FAQ',
      event_label: question,
      action: action, // 'expand' 或 'collapse'
      question: question,
      page_path: pagePath,
    });
  }
};

/**
 * 跟踪CTA按钮点击
 * @param {string} ctaText - CTA按钮文本
 * @param {string} ctaLocation - CTA位置（如 'hero', 'section', 'footer'）
 * @param {string} pagePath - 当前页面路径
 */
export const trackCTAClick = (ctaText, ctaLocation, pagePath = window.location.pathname) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'cta_click', {
      event_category: 'CTA',
      event_label: ctaText,
      cta_text: ctaText,
      cta_location: ctaLocation,
      page_path: pagePath,
    });
  }
};

/**
 * 跟踪内部链接点击
 * @param {string} linkText - 链接文本
 * @param {string} linkPath - 链接路径
 * @param {string} sourcePage - 来源页面
 */
export const trackInternalLink = (linkText, linkPath, sourcePage = window.location.pathname) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'internal_link_click', {
      event_category: 'Internal Linking',
      event_label: linkText,
      link_text: linkText,
      link_path: linkPath,
      source_page: sourcePage,
    });
  }
};

/**
 * 记录AI答案结果中的问题（用于AEO分析）
 * 这个函数用于记录哪些问题出现在AI答案结果中
 * @param {Object} logData - 日志数据对象
 */
export const logAIAnswerResult = (logData) => {
  // 这里可以发送到后端API或本地存储
  // 示例：发送到后端API
  if (typeof window !== 'undefined') {
    // 存储到 localStorage 作为示例（生产环境应发送到后端）
    const logs = JSON.parse(localStorage.getItem('aeo_ai_answer_logs') || '[]');
    logs.push({
      ...logData,
      timestamp: new Date().toISOString(),
      user_agent: navigator.userAgent,
      page_url: window.location.href,
    });
    
    // 限制日志数量（保留最近1000条）
    if (logs.length > 1000) {
      logs.splice(0, logs.length - 1000);
    }
    
    localStorage.setItem('aeo_ai_answer_logs', JSON.stringify(logs));
    
    // 如果配置了后端API，可以发送数据
    // fetch('/api/aeo/logs', {
    //   method: 'POST',
    //   headers: { 'Content-Type': 'application/json' },
    //   body: JSON.stringify(logData),
    // });
  }
};

/**
 * 获取AI答案日志（用于分析）
 * @returns {Array} 日志数组
 */
export const getAIAnswerLogs = () => {
  if (typeof window !== 'undefined') {
    return JSON.parse(localStorage.getItem('aeo_ai_answer_logs') || '[]');
  }
  return [];
};

/**
 * 清除AI答案日志
 */
export const clearAIAnswerLogs = () => {
  if (typeof window !== 'undefined') {
    localStorage.removeItem('aeo_ai_answer_logs');
  }
};

/**
 * 导出日志为JSON文件
 * @param {string} filename - 文件名（可选）
 */
export const exportLogsAsJSON = (filename = 'aeo_ai_answer_logs.json') => {
  const logs = getAIAnswerLogs();
  const dataStr = JSON.stringify(logs, null, 2);
  const dataBlob = new Blob([dataStr], { type: 'application/json' });
  const url = URL.createObjectURL(dataBlob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Google Ads 网页浏览 Conversion Event
 */
export const gtag_report_conversion = (url) => {
  const callback = function () {
    if (typeof url !== 'undefined') {
      window.location = url;
    }
  };
  if (typeof window.gtag === 'function') {
    window.gtag('event', 'conversion', {
      'send_to': 'AW-18458381832/6SnuCPThvpcdEIic0uFE',
      'value': 1.0,
      'currency': 'CAD',
      'event_callback': callback
    });
  }
  return false;
};

if (typeof window !== 'undefined') {
  window.gtag_report_conversion = gtag_report_conversion;
}

